<script setup lang="ts">
import Loading from '@legacy/components/common/Loading.vue';
import axios from 'axios';
import { Check, Clipboard, Info } from 'lucide';
import { onMounted, ref, watch } from 'vue';
import { SphButton } from '@/components/base/button';
import SphDialog from '@/components/base/dialog';
import { SphIcon } from '@/components/base/icon';
import SphCodeInput from '@/components/compound/code-input/CodeInput.vue';
import { useI18n } from '@/composables/i18n';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps({
    loginContextId: {
        type: Number,
        required: true,
    },
});
const emit = defineEmits(['cancel', 'complete']);
interface SetupData {
    qrCode: string
    secret: string
}
const { i18n } = useI18n();

const isOpen = ref(true);
const loading = ref(true);
const setupData = ref<SetupData | null>(null);
const verificationCode = ref<string | null>(null);
const error = ref<string | null>(null);
const verifying = ref(false);
const showSecret = ref(false);
const copied = ref(false);

async function startSetup() {
    loading.value = true;
    error.value = null;

    try {
        const response = await axios.post(`/2fa/${props.loginContextId}/setup`);
        if (response.data.data?.setup) {
            setupData.value = response.data.data.setup;
        }
    } catch (e: any) {
        error.value = e.response?.data?.message || i18n.t('Setup fehlgeschlagen');
    } finally {
        loading.value = false;
    }
}

async function verify({ done }: any) {
    if (!verificationCode.value) {
        error.value = i18n.t('Bitte gib einen 6-stelligen Code ein');
        done();
        return;
    }

    verifying.value = true;
    error.value = null;

    try {
        await axios.post(`/2fa/${props.loginContextId}/verify-setup`, {
            code: verificationCode.value,
        });
        done(true);
    } catch (e: any) {
        error.value = e.response?.data?.message || i18n.t('Verifizierung fehlgeschlagen');
        verificationCode.value = null;
        done();
    } finally {
        verifying.value = false;
    }
}

function close() {
    emit('cancel');
}

async function copySecret() {
    const text = setupData.value?.secret;
    if (!text) {
        return;
    }

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
            await navigator.clipboard.writeText(text);
            copied.value = true;
            setTimeout(() => {
                copied.value = false;
            }, 2000);
        } catch (err) {
            console.error('Clipboard write failed', err);
        }
    }
}

onMounted(() => {
    startSetup();
});

watch(() => verificationCode.value, (code) => {
    if (code && code.length === 6) {
        verify({ done: (result: boolean | null) => {
            if (result) {
                emit('complete');
            }
        } });
    }
});
</script>

<template>
    <SphDialog
        v-model:state="isOpen"
        variant="form"
        :title="$t('Zwei-Faktor-Authentifizierung einrichten')"
        :submit-button-label="verifying ? $t('Wird geprüft...') : $t('Aktivieren')"
        :disable-submit="!verificationCode || verifying"
        @submit="verify"
        @close="close"
    >
        <Loading v-if="loading" />

        <div v-else-if="setupData">
            <p class="kgr-text leading-tight text-sm md:text-base">
                {{ $t('Der QR-Code lässt sich mit einer Authenticator-App (z.B. Google Authenticator, Authy) scannen.') }}
            </p>

            <div class="flex justify-center mt-4 mb-2">
                <img
                    :src="setupData.qrCode"
                    alt="QR Code"
                    class="qr-code"
                />
            </div>

            <div class="kgr-text kgr-text--centered">
                <button
                    type="button"
                    class="kgr-clickable kgr-text kgr-text--small kgr-link"
                    @click="showSecret = !showSecret"
                >
                    {{ showSecret ? $t('Schlüssel ausblenden') : $t('Schlüssel manuell eingeben') }}
                </button>

                <div
                    v-if="showSecret"
                    class="flex flex-col gap-4 mt-1 justify-center items-center mx-4"
                >
                    <div class="flex items-center gap-0 mt-4">
                        <code class="secret-code p-2 mx-0 mb-0">{{ setupData.secret }}</code>
                        <SphButton
                            variant="ghost"
                            :icon="copied ? Check : Clipboard"
                            :title="$t('Ins Clipboard kopieren')"
                            @click="copySecret"
                        />
                    </div>
                    <div class="mt-0 text-muted-foreground text-sm flex items-center gap-x-2">
                        <SphIcon :icon="Info" />
                        {{ $t('Wähle bei der manuellen Eingabe "zeitbasiert".') }}
                    </div>
                </div>
            </div>

            <hr class="kgr-separator kgr-separator--light" />

            <p class="mb-4 leading-tight text-sm text-center">
                {{ $t('Eingabe des 6-stelligen Codes aus der App:') }}
            </p>

            <div class="kgr-inputGroup flex justify-center">
                <SphCodeInput
                    v-model:model-value="verificationCode"
                    :length="6"
                    pattern="[0-9]*"
                    inputmode="decimal"
                    :disabled="verifying"
                    @input="(str: string | null) => str ? error = null : ''"
                />
            </div>

            <div
                v-if="error"
                class="kgr-inputGroup__text kgr-inputGroup__text--error text-center mt-4"
            >
                {{ error }}
            </div>
        </div>

        <div v-else-if="error">
            <p class="kgr-inputGroup__text kgr-inputGroup__text--error">
                {{ error }}
            </p>
        </div>
    </SphDialog>
</template>

<style scoped lang="scss">
.qr-code {
    border: 1px solid #ddd;
    border-radius: 4px;
}

.secret-code {
    font-family: monospace;
    background: #f5f5f5;
    letter-spacing: 0;
    font-size: 8.5px;

    @media (min-width: 480px) {
        letter-spacing: 0.05em;
        font-size: 11px;
    }
}
</style>
